// utils/useUserRoles.js
import { useState, useEffect } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { logger } from './logger';

// localStorage key for caching roles
const ROLES_CACHE_KEY = 'augustsays_user_roles';

// Helper functions for localStorage
const saveRolesToStorage = (accountId, roles, source) => {
    try {
        const roleData = {
            accountId,
            roles,
            source,
            timestamp: Date.now(),
            expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours expiry
        };
        localStorage.setItem(ROLES_CACHE_KEY, JSON.stringify(roleData));
    } catch (error) {
        logger.warn('Failed to save roles to localStorage:', error);
    }
};

const loadRolesFromStorage = (accountId) => {
    try {
        const stored = localStorage.getItem(ROLES_CACHE_KEY);
        if (!stored) return null;
        
        const roleData = JSON.parse(stored);
        
        // Check if data is for the same account and not expired
        if (roleData.accountId === accountId && roleData.expiresAt > Date.now()) {
            return {
                roles: roleData.roles || [],
                source: roleData.source,
                isFromCache: true
            };
        }
        
        // Clean up expired data
        localStorage.removeItem(ROLES_CACHE_KEY);
        return null;
    } catch (error) {
        logger.warn('Failed to load roles from localStorage:', error);
        localStorage.removeItem(ROLES_CACHE_KEY);
        return null;
    }
};

const clearRolesFromStorage = () => {
    try {
        localStorage.removeItem(ROLES_CACHE_KEY);
    } catch (error) {
        logger.warn('Failed to clear roles from localStorage:', error);
    }
};

/**
 * Custom hook to manage user roles from MSAL with localStorage caching
 * Handles timing issues and provides a consistent interface for role checking
 */
export const useUserRoles = () => {
    const { instance, inProgress } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const account = useAccount(activeAccount);
    
    const [tokenRoles, setTokenRoles] = useState([]);
    const [isLoadingRoles, setIsLoadingRoles] = useState(false);
    const [hasAttemptedTokenFetch, setHasAttemptedTokenFetch] = useState(false);
    const [cachedRoles, setCachedRoles] = useState([]);
    const [rolesSource, setRolesSource] = useState('none');

    // Get application roles from idTokenClaims first
    const idTokenRoles = account?.idTokenClaims?.roles || [];
    
    // Load roles from localStorage on mount
    useEffect(() => {
        if (account?.localAccountId) {
            const storedRoles = loadRolesFromStorage(account.localAccountId);
            if (storedRoles) {
                setCachedRoles(storedRoles.roles);
                setRolesSource(storedRoles.source + '_cached');
                logger.log('Loaded roles from localStorage:', storedRoles.roles);
            }
        }
    }, [account?.localAccountId]);

    // Alternative method to get roles from access token if idTokenClaims fails
    const getAccessTokenWithRoles = async () => {
        if (!account || idTokenRoles.length > 0 || hasAttemptedTokenFetch) return;
        
        setIsLoadingRoles(true);
        setHasAttemptedTokenFetch(true);
        
        try {
            const request = {
                scopes: [instance.getConfiguration().auth.clientId],
                account: account
            };
            const response = await instance.acquireTokenSilent(request);
            
            if (response.idToken) {
                const tokenParts = response.idToken.split('.');
                if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    logger.log('Token payload from acquireTokenSilent:', payload);
                    if (payload.roles) {
                        setTokenRoles(payload.roles);
                        setRolesSource('accessToken');
                        logger.log('Roles found in access token:', payload.roles);
                        
                        // Save to localStorage
                        saveRolesToStorage(account.localAccountId, payload.roles, 'accessToken');
                    }
                }
            }
        } catch (error) {
            logger.error("Failed to acquire token with roles:", error);
        } finally {
            setIsLoadingRoles(false);
        }
    };

    // Save idToken roles to localStorage when available
    useEffect(() => {
        if (account?.localAccountId && idTokenRoles.length > 0) {
            setRolesSource('idToken');
            saveRolesToStorage(account.localAccountId, idTokenRoles, 'idToken');
            logger.log('Saved idToken roles to localStorage:', idTokenRoles);
        }
    }, [account?.localAccountId, idTokenRoles.length]);

    // Effect to handle timing issues with MSAL token processing
    useEffect(() => {
        if (account && inProgress === 'none' && idTokenRoles.length === 0 && !hasAttemptedTokenFetch && !isLoadingRoles && cachedRoles.length === 0) {
            // First attempt after 250ms (quick check)
            const firstTimeoutId = setTimeout(() => {
                if (idTokenRoles.length === 0) {
                    // Second attempt after additional 750ms (1000ms total)
                    const secondTimeoutId = setTimeout(() => {
                        getAccessTokenWithRoles();
                    }, 1050);
                    
                    return () => clearTimeout(secondTimeoutId);
                }
            }, 50);
            
            return () => clearTimeout(firstTimeoutId);
        }
    }, [account, inProgress, idTokenRoles.length, hasAttemptedTokenFetch, isLoadingRoles, cachedRoles.length]);

    // Reset attempt flag when account changes
    useEffect(() => {
        if (account?.localAccountId) {
            // Clear cache if account changed
            const storedRoles = loadRolesFromStorage(account.localAccountId);
            if (!storedRoles) {
                setCachedRoles([]);
                setRolesSource('none');
            }
        } else {
            // Clear everything when no account
            setCachedRoles([]);
            setRolesSource('none');
            clearRolesFromStorage();
        }
        
        setHasAttemptedTokenFetch(false);
        setTokenRoles([]);
    }, [account?.localAccountId]);

    // Computed values - prioritize fresh data over cached
    const userRoles = idTokenRoles.length > 0 ? idTokenRoles : 
                     tokenRoles.length > 0 ? tokenRoles : 
                     cachedRoles;
    
    const primaryRole = userRoles.length > 0 ? userRoles[0] : null;
    const isLoading = inProgress !== 'none' || isLoadingRoles;

    // Helper functions for common role checks
    const hasRole = (roleName) => userRoles.includes(roleName);
    const hasAnyRole = (roleNames) => roleNames.some(role => userRoles.includes(role));
    const hasAllRoles = (roleNames) => roleNames.every(role => userRoles.includes(role));

    // Specific role checks (add more as needed)
    const canDownload = hasRole('SurveyDownloader');
    const isAdmin = hasRole('staging_administrator') || hasRole('production_administrator');
    const isViewer = hasRole('Survey Viewer');

    // Debug logging
    if (account && process.env.NODE_ENV === 'development') {
        logger.log('useUserRoles Debug:', {
            inProgress,
            idTokenRoles,
            tokenRoles,
            cachedRoles,
            userRoles,
            rolesSource,
            canDownload,
            hasAttemptedTokenFetch
        });
    }

    return {
        // Raw data
        account,
        userRoles,
        primaryRole,
        isLoading,
        isLoadingRoles,
        rolesSource,
        
        // Helper functions
        hasRole,
        hasAnyRole,
        hasAllRoles,
        
        // Specific role checks
        canDownload,
        isAdmin,
        isViewer,
        
        // Add the missing property for dashboard
        isRolesLoaded: !isLoading && (idTokenRoles.length > 0 || tokenRoles.length > 0 || cachedRoles.length > 0 || hasAttemptedTokenFetch),
        
        // Utility
        accessLevel: userRoles.length > 0 ? userRoles.join(', ') : 'None',
        roleCount: userRoles.length
    };
};